<script setup>
const { isMobile } = useDevice();

const data = await import(
  `../../../public/content/services/development-services/ecommerce-development-content.json`
);

useHead({
  title: data?.meta_tags?.title || "",
  meta: data?.meta_tags?.meta || [],
});

const visible = ref(false);

const formPrefilledData = ref(null);

const openModal = (prefilledValue = null) => {
  if (prefilledValue == null) formPrefilledData.value = null;
  else if (prefilledValue.length) formPrefilledData.value = prefilledValue;
  visible.value = true;
};
</script>

<template>
  <section class="max-w-screen-xl px-4 mx-auto md:mt-10">
    <div class="flex max-md:flex-col justify-between gap-10">
      <WrServicesHeroSection
        :heading="data?.header.heading"
        :description="data?.header.text"
        :src="data?.header.image"
        :alt="data?.header.heading"
        width="497"
        height="429"
        @open-modal="openModal"
      />
    </div>
  </section>

  <section class="dark:bg-none py-20 lg:py-36">
    <div class="max-w-screen-xl px-4 mx-auto">
      <WrServicesPromiseSection :data="data.our_promises" />
    </div>
  </section>

  <section class="dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="lg:text-center my-6">
        <WrGradientText>
          {{ data.our_services.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px]"
      >
        {{ data.our_services.subheading }}
      </p>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-20 mt-12">
        <ServiceValueCard
          v-for="service in data.our_services.expertisetext"
          :img="service.image"
          :alt="service.heading"
          :title="service.heading"
          :description="service.text"
        />
      </div>
    </div>
  </section>

  <section class="dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="lg:text-center">
        <WrGradientText>
          {{ data.ecommerce_domains.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px] mb-10"
      >
        {{ data.ecommerce_domains.subheading }}
      </p>
      <!--Section | Our e-commerce solutions to operational needs. Starts-->
      <WrStepper
        :steps="data.ecommerce_domains.steps"
        :layout="isMobile ? 'mobile' : 'desktop'"
      />
    </div>
  </section>

  <section class="dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="lg:text-center my-6">
        <WrGradientText>
          {{ data.framework_benefits.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px]"
      >
        {{ data.framework_benefits.subheading }}
      </p>

      <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-20 mt-12">
        <ServiceValueCard
          v-for="(item, idx) in data.framework_benefits.benefit_items"
          :key="item.title"
          :img="item.image"
          :alt="item.title"
          :title="item.title"
          :description="item.text"
        />
      </div>
    </div>
  </section>

  <section class="py-6 lg:py-12">
    <div class="px-4 max-w-screen-xl mx-auto lg:text-center">
      <h2>
        <WrGradientText
          v-html="data.all_major_platform.heading"
        ></WrGradientText>
      </h2>
      <p
        v-if="data.all_major_platform.subheading"
        v-html="data.all_major_platform.subheading"
        class="tracking-[0.5px] mb-10 mx-auto lg:px-20"
      ></p>

      <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <CardFunctionality
          v-for="(item, i) in data.all_major_platform.steps"
          :key="i"
          :img="item.image"
          :title="item.heading"
          :description="item.text"
        />
      </div>
    </div>
  </section>

  <section class="dark:bg-transparent">
    <div class="max-w-screen-xl mx-auto py-10 md:py-20 px-4 lg:px-8">
      <WrCardCTA
        append-class="max-md:px-4 py-8"
        :title="data.project_in_mind.heading"
        :subtitle="data.project_in_mind.small_heading"
        :description="data.project_in_mind.subheading"
        :buttonText="data.project_in_mind.linkText"
        :icon="data.project_in_mind.image"
      />
    </div>
  </section>

  <section class="dark:bg-none section-spacing">
    <div class="container-wrapper-xl">
      <WrImageAndCardSection :data="data?.partnering" />
      <WrGoogleRating />
    </div>
  </section>

  <section class="dark:bg-transparent">
    <div class="max-w-screen-xl mx-auto py-20 px-4 lg:px-8">
      <WrCardCTA
        append-class="max-md:px-4 py-8"
        :title="data.contact_us_bar.heading"
        :subtitle="data.contact_us_bar.small_heading"
        :description="data.contact_us_bar.subheading"
        :buttonText="data.contact_us_bar.linkText"
        imgPosition="left"
        :icon="data.contact_us_bar.image"
      />
    </div>
  </section>

  <section class="dark:bg-none py-10 lg:py-20">
    <div class="max-w-screen-xl mx-auto px-4">
      <h2 class="lg:text-center">
        <WrGradientText>
          {{ data.development_process.heading }}
        </WrGradientText>
      </h2>
      <p
        class="max-w-screen-lg mx-auto lg:text-center lg:text-xl tracking-[0.5px] mb-10"
      >
        {{ data.development_process.subheading }}
      </p>
      <!--Section | Our e-commerce solutions to operational needs. Starts-->
      <WrStepper
        :steps="data.development_process.steps"
        :layout="isMobile ? 'mobile' : 'desktop'"
      />
    </div>
  </section>

  <section class="dark:bg-none py-10 lg:py-20">
    <div
      class="max-w-screen-xl px-4 mx-auto flex max-lg:flex-col justify-between items-center gap-14 lg:gap-32 lg:*:w-1/2"
    >
      <div>
        <h2 class="text-3xl lg:text-4xl mb-2 max-md:mb-4 md:mb-4">
          <WrGradientText
            :text="data.contact_us_bar2.heading"
            append-class="pb-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-1.57%,_var(--text-gradient-2)_30.72%)]"
          />
        </h2>
        <p class="lg:text-xl tracking-[0.5px]">
          {{ data.contact_us_bar2.subheading }}
        </p>
      </div>
      <WrCardCTA
        append-class="lg:py-14 lg:mr-8"
        :icon="data.contact_us_bar2.image"
        alt="project icon"
        :subtitle="data.contact_us_bar2.box.small_heading"
        :title="data.contact_us_bar2.heading"
        :description="data.contact_us_bar2.box.subheading"
        :buttonText="data.contact_us_bar2.box.btnText"
      />
    </div>
  </section>

  <section class="px-4 relative z-10 py-10 lg:py-20">
    <div class="max-w-screen-xl md:px-4 mx-auto">
      <h3 class="text-center leading-tight relative xl:px-0 lg:px-20">
           <WrGradientText
            :text="data.technology_stack.heading"
            append-class="py-0 md:py-4"
          />
      </h3>
       
      <p class="xl:mt-4 text-base text-center max-md:px-0 !leading-[1.8]">
        {{ data.technology_stack.subheading }}
      </p>
      <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
        <CardTechnologyStack
          v-for="stac in data.technology_stack.stacks"
          :img="stac.image"
          :title="stac.title"
          :stacs="stac.list"
          :layout-change="true"
        />
      </div>
    </div>
  </section>

  <section>
    <div
      class="max-w-screen-xl mx-auto px-4 w-full grid md:grid-cols-2 gap-6 relative z-10 pb-10 lg:pb-24 mt-20"
    >
      <div
        class="size-[322px] absolute top-40 left-10 rounded-full blur-[150px] bg-[linear-gradient(180deg,_rgba(73,2,166,0.2)_0%,_rgba(255,164,235,0.2)_100%)]"
      ></div>
      <div class="max-md:text-center mx-auto max-w-[504px] z-10 relative">
        <h3>
          <WrGradientText
            text="Get all your questions"
            append-class="py-0 md:py-4"
          />
          <span>answered here</span>
        </h3>
        <p class="mt-5 pr-4 text-lg tracking-[0.5px]">
          {{ data.faq?.subheading }}
        </p>
      </div>
      <div class="grow">
        <WrAccordion :Content="data.faq.content" />
      </div>
    </div>
  </section>
  <!--  Frequently asked questions Ends-->

  <!-- Consider hiring remote developers for your next eCommerce project.. Starts-->
  <section class="dark:bg-none py-20">
    <div
      class="max-w-screen-xl px-4 mx-auto flex max-lg:flex-col justify-between gap-14"
    >
      <div class="lg:w-[45%]">
        <h2 class="text-3xl mb-2 max-md:mb-4 md:mb-4">
          <WrGradientText
            :text="data.hire_us.heading"
            append-class="py-0 lg:pb-2 bg-clip-text bg-[linear-gradient(92.15deg,_var(--text-gradient-1)_-1.57%,_var(--text-gradient-2)_30.72%)]"
          />
        </h2>
        <p class="text-xl max-md:text-sm tracking-[0.5px]">
          {{ data.hire_us.subheading }}
        </p>
      </div>
      <div class="grid sm:grid-cols-2 gap-4">
        <!-- Hourly Basis -->
        <WrCardHiring
          @openModal="openModal"
          v-for="(card, index) in data.hire_us.cards"
          :key="index"
          :title="card.type"
          :description="card.billing"
          :badgeText="card.badgeText"
          :offerText="
            index === 0
              ? 'Get <strong>5 hrs</strong> free on purchase of <strong>100 hrs.</strong>'
              : ''
          "
          :icon="card.image"
          :showCounter="card.showcounter"
          :defaultCount="parseInt(parseInt(card?.defaultCount))"
          :page="{ name: 'eCommerce' }"
          :index="index"
          :buttonLabel="card.buttonLabel"
        />
      </div>
    </div>
  </section>
  <!--  Consider hiring remote developers for your next eCommerce project. Ends-->

  <!-- We have many more resources Starts-->
  <section class="max-w-screen-xl mx-auto px-4 py-6">
    <h2 class="lg:text-center">
      <WrGradientText append-class="leading-[1]">
        {{ data.other_services.side_box.heading }}
      </WrGradientText>
    </h2>
    <p
      class="max-w-screen-md mx-auto lg:text-lg lg:text-center tracking-[0.5px] mb-5 mt-2"
    >
      {{ data.other_services.side_box.subheading }}
    </p>
    <WrFrameworksCarousel />
  </section>
  <!-- We have many more resources Ends-->

  <!-- Testimonials Starts-->
  <section
    class="relative py-10 lg:py-20 overflow-hidden max-w-screen-xl mx-auto px-4 z-20"
  >
    <h2 class="text-center">
      <WrGradientText text="Trusted for excellence and efficiency" />
    </h2>
    <p
      class="text-lg text-[var(--foreground)] mb-10 text-center tracking-[0.5px]"
    >
      Webreinvent is trusted by leading organisations to enhance their
      development and operational efficiency.
    </p>
    <!-- Carousel Section -->

    <WrTestimonialCardCarousel />

    <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-2"
      >View All</WrButton
    >
  </section>
  <WrContactFormPopup
    :isOpen="visible"
    @close="visible = false"
    class="z-30"
    :prefilledContent="formPrefilledData"
  />
</template>
